import React, { Fragment, useEffect, useState } from 'react'
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import Divider from '@material-ui/core/Divider'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { Hidden, useMediaQuery } from '@material-ui/core'
import DoneIcon from '@material-ui/icons/Done'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import { useHistory } from 'react-router-dom'
import { Menu } from '../types/Menu'
import { DISCLAIMER_URL, PRIVACY_URL, TERMS_URL } from './footer/Footer'
import TodayIcon from '@material-ui/icons/Today'

const drawerWidth = 240

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window
  mobileOpen: boolean
  handleDrawerToggle: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: theme.mixins.toolbar,
    drawer: {
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
        flexShrink: 0,
      },
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawerContainer: {
      overflow: 'auto',
    },
  })
)

const getInitialMenus = (path: string): Menu[] => [
  {
    name: 'To do',
    path: '/todo',
    type: 'primary',
    icon: <DoneIcon />,
    isActive: path === '/' || path === '/todo',
  },
  {
    name: 'Keep',
    path: '/keep',
    type: 'primary',
    icon: <BookmarkBorderIcon />,
    isActive: path === '/keep',
  },
  {
    name: 'Calendar',
    path: '/calendar',
    type: 'primary',
    icon: <TodayIcon />,
    isActive: path.startsWith('/calendar'),
  },
  {
    name: 'Languages',
    path: '/memo',
    type: 'primary',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="currentColor"
        className="bi bi-chat-dots"
        viewBox="0 0 16 16"
      >
        <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
        <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a10.97 10.97 0 0 0 .398-2z" />
      </svg>
    ),
    isActive: path.startsWith('/memo'),
  },
  // {
  //   name: 'Suggestion',
  //   path: '/suggestion',
  //   type: 'primary',
  //   icon: (
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       width="24"
  //       height="24"
  //       fill="currentColor"
  //       className="bi bi-lightbulb"
  //       viewBox="0 0 16 16"
  //     >
  //       <path d="M2 6a6 6 0 1 1 10.174 4.31c-.203.196-.359.4-.453.619l-.762 1.769A.5.5 0 0 1 10.5 13a.5.5 0 0 1 0 1 .5.5 0 0 1 0 1l-.224.447a1 1 0 0 1-.894.553H6.618a1 1 0 0 1-.894-.553L5.5 15a.5.5 0 0 1 0-1 .5.5 0 0 1 0-1 .5.5 0 0 1-.46-.302l-.761-1.77a1.964 1.964 0 0 0-.453-.618A5.984 5.984 0 0 1 2 6zm6-5a5 5 0 0 0-3.479 8.592c.263.254.514.564.676.941L5.83 12h4.342l.632-1.467c.162-.377.413-.687.676-.941A5 5 0 0 0 8 1z" />
  //     </svg>
  //   ),
  //   isActive: path.startsWith('/suggestion')
  // },
  {
    name: 'Terms',
    path: TERMS_URL,
    type: 'secondary',
    isActive: path === TERMS_URL,
  },
  {
    name: 'Privacy',
    path: PRIVACY_URL,
    type: 'secondary',
    isActive: path === PRIVACY_URL,
  },
  {
    name: 'Disclaimer and Legal Notices',
    path: DISCLAIMER_URL,
    type: 'secondary',
    isActive: path === DISCLAIMER_URL,
  },
]

export default function LeftSideBar(props: Props): JSX.Element {
  const { window, mobileOpen, handleDrawerToggle } = props
  const [menus, setMenus] = useState<Menu[]>([])
  const history = useHistory()
  const theme = useTheme()
  const matchesSM: boolean = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    setMenus(getInitialMenus(history.location.pathname))
  }, [history.location.pathname])

  const handleMenuClick = (menu: Menu) => {
    history.push(menu.path)
    const copied = [...menus]
    copied.forEach((ele) => (ele.isActive = false))
    const found = copied.find((ele) => ele.name === menu.name)
    if (!found) {
      return
    }
    found.isActive = true
    setMenus(copied)
    if (matchesSM) {
      setTimeout(() => {
        handleDrawerToggle()
      }, 50)
    }
  }

  const classes = useStyles()
  const drawer = (
    <div>
      <div className={classes.toolbar}>
        <List>
          <ListItem>
            <img
              src={
                'https://ik.imagekit.io/crso3ztbifq/todobe/drawer_logo_FpBv0V7B9Y.png'
              }
              alt={'drawer_main_logo'}
              height={'20px'}
            />
          </ListItem>
        </List>
      </div>
      <Divider />
      <List>
        {menus.map((menu, index) => (
          <Fragment key={index}>
            <ListItem
              button
              selected={menu.isActive}
              onClick={() => handleMenuClick(menu)}
            >
              {menu.type === 'primary' && (
                <ListItemIcon>{menu.icon}</ListItemIcon>
              )}
              {menu.type === 'primary' ? (
                <ListItemText primary={menu.name} />
              ) : (
                <ListItemText secondary={menu.name} />
              )}
            </ListItem>
            {index === 1 && <Divider />}
          </Fragment>
        ))}
      </List>
    </div>
  )

  const container =
    window !== undefined ? () => window().document.body : undefined

  return (
    <nav className={classes.drawer} aria-label="mailbox folders">
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Hidden smUp implementation="css">
        <Drawer
          container={container}
          className={classes.drawer}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          {drawer}
        </Drawer>
      </Hidden>
      <Hidden xsDown implementation="css">
        <Drawer
          classes={{
            paper: classes.drawerPaper,
          }}
          variant="permanent"
          open
        >
          {drawer}
        </Drawer>
      </Hidden>
    </nav>
  )
}
